import { MessageCircle, Phone, X, Users } from "lucide-react";
import { motion } from "framer-motion";
import { useConfig } from "../contexts/config.context";
import { ChatInterface } from "./chat-interface";
import { mergeClasses } from "../lib/mergeClasses";
import { ChatWidgetProps } from "./types";

function getGreetingByLocalTime() {
  const lHour = new Date().getHours()

  switch (true) {
    case lHour < 12: {
      return 'morning'
    }
    case lHour < 15: {
      return 'afternoon'
    }
    default: {
      return 'evening'
    }
  }
}

export default function ChatWidget({
  onClose,
  onCallNow,
  onChatNow,
  setShowCall,
  setShowChat,
  connecting,
  connected,
  isThinking,
  isPTTActive,

  chatHistory,
  handleDisconnect,
  handleTextInput,
  handlePTTStart,
  handlePTTEnd,
}: ChatWidgetProps) {
  const { config } = useConfig()

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.95 }}
      id="chat-widget-container"
    >
      {
        connected && (
          <ChatInterface
            key="chat-interface"
            handleSubmitTextInput={handleTextInput}
            isCallConnected={true}
            isThinking={isThinking}
            isPTTActive={isPTTActive}
            chatHistory={chatHistory}
            onCallEnd={handleDisconnect}
            handlePTTStart={handlePTTStart}
            handlePTTEnd={handlePTTEnd}
          />
        )
      }

      {/* Header fixed over the image */}
      <div className={mergeClasses("absolute top-0 left-0 right-0 z-20 flex justify-between items-center p-4 pointer-events-auto", {
        hidden: connected,
      })}>
        <div className="!bg-[var(--sonaris-color-secondary)] rounded-full px-4 py-1">
          <span className="text-xs tracking-widest !text-[var(--sonaris-color-text)] font-bold uppercase sonaris-montserrat">sonaris.ai</span>
          <div className="w-8 h-8 relative hidden">
            <svg viewBox="0 0 24 24" fill="none" className="text-white">
              <path
                d="M19 10v2a7 7 0 0 1-14 0v-2M3 10a9 9 0 0 1 18 0M8 18v-3a1 1 0 0 1 1-1h6a1 1 0 0 1 1 1v3"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M12 4a2 2 0 1 1 0 4 2 2 0 0 1 0-4z"
                fill="currentColor"
              />
            </svg>
          </div>
        </div>

        <button
          id="btn-close"
          onClick={onClose}
        >
          <X className="w-6 h-6" />
        </button>
      </div>


      {/* Background image with gradient overlay */}
      <div className={mergeClasses("h-[240px] z-0 overflow-hidden rounded-t-3xl relative", {
        hidden: connected,
      })}>
        <img
          src={config.remote?.character.avatar || "https://assets.sonaris.ai/static/avatar.png"}
          alt={config.remote?.character.name || "Artifical Intelligence"}
          className="w-full h-full object-cover object-[center_5%] block"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-[var(--sonaris-color-primary)]/40 to-[var(--sonaris-color-primary)]"></div>
      </div>

      {/* Main content below the image */}
      <div className={mergeClasses("relative z-10 px-3 bg-gradient-to-b from-[var(--sonaris-color-primary)] to-[var(--sonaris-color-secondary)]", {
        hidden: connected
      })}>
        {/* Talk to AI section */}
        <div className="!bg-[var(--sonaris-color-secondary)]/30 backdrop-blur-sm rounded-2xl p-3 mb-10">
          <div className="grid grid-rows-2 grid-cols-4 items-center gap-1">
            <div className="col-span-1 row-span-full relative">
              <div id="character-image-container">
                <img
                  src={config.remote?.character.avatar || "https://assets.sonaris.ai/static/avatar.png"}
                  alt="AI Assistant"
                />
              </div>
              <div className="absolute bottom-1 right-4 bg-green-400 w-3 h-3 rounded-full border border-white"></div>
            </div>
            <span className="text-white text-base font-normal col-span-3">
              Talk to <span className="capitalize">{config.remote?.character.name || "AI"}</span>
            </span>
            <div id="cta-buttons-flex" className="flex ml-auto gap-2">
              <button
                className="!bg-white !hover:bg-white/90 disabled:opacity-70 disabled:cursor-not-allowed"
                onClick={() => {
                  onCallNow();
                  setShowCall(true)
                }}
                disabled={connecting}
              >
                <Phone className="w-4 h-4 mr-1.5" />
                Talk
              </button>
              <button className="!bg-transparent !text-white !hover:bg-white/10"
                onClick={() => {
                  onChatNow();
                  setShowChat(true)
                }}
                disabled={connecting}
              >
                <MessageCircle className="w-4 h-4 mr-1.5" />
                Chat
              </button>
            </div>
          </div>
        </div>


        {/* Greeting section */}
        <h1 className="text-white text-3xl font-bold mb-1 px-3">Good {getGreetingByLocalTime()}!</h1>
        <p className="text-white/90 text-lg !mb-10 px-3">
          How can we help you today?
        </p>

        {/* User count */}
        <div className="flex items-center justify-center mb-3">
          <Users className="w-4 h-4 text-white/70 mr-2" />
          <span className="text-white/70 text-xs">
            Join 10,000+ users getting help daily
          </span>
        </div>

        {/* Powered by */}
        <div className="flex justify-center items-center text-xs text-white/70 pb-4">
          <span>Powered by</span>
          <span className="ml-1 font-medium flex items-center">
            <svg viewBox="0 0 24 24" fill="none" className="w-3 h-3 mr-1">
              <circle
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="2"
              />
              <path
                d="M8 12h8M12 8v8"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
              />
            </svg>
            <a href="https://sonaris.ai" className="!text-inherit underline" target="_blank">Sonaris.ai</a>
          </span>
        </div>
      </div>

      {/* Decorative circles */}
      <div className={mergeClasses("absolute top-20 right-4 w-16 h-16 rounded-full bg-white/5", {
        hidden: connected
      })}></div>
      <div className={mergeClasses("absolute bottom-40 left-4 w-10 h-10 rounded-full bg-white/5", {
        hidden: connected
      })}></div>
    </motion.div>
  );
}
